import { motion } from 'framer-motion';
import { ArrowLeft, Layers3, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../layout/Header';

type SubjectCard = {
  id: string;
  title: string;
  responsible: string;
  avatar: string;
  description: string;
};

const subjectCards: SubjectCard[] = [
  {
    id: 'dany',
    title: 'Idées Dany',
    responsible: 'Dany Bourassa',
    avatar: '/DB.png',
    description: 'Espace réservé aux idées, constats et propositions de Dany.',
  },
  {
    id: 'francois',
    title: 'Idées François',
    responsible: 'François Delisle',
    avatar: '/FP.png',
    description: 'Espace réservé aux idées, constats et propositions de François.',
  },
  {
    id: 'jean-pierre',
    title: 'Idées Jean-Pierre',
    responsible: 'Jean-Pierre Leclerc',
    avatar: '/JP.png',
    description: 'Espace réservé aux idées, constats et propositions de Jean-Pierre.',
  },
  {
    id: 'jean-michel',
    title: 'Idées Jean-Michel',
    responsible: 'Jean-Michel Caouette',
    avatar: '/JMC.png',
    description: 'Espace réservé aux idées, constats et propositions de Jean-Michel.',
  },
  {
    id: 'eric',
    title: 'Idées Eric',
    responsible: 'Eric Hains',
    avatar: '/EH.png',
    description: 'Espace réservé aux idées, constats et propositions d’Eric.',
  },
  {
    id: 'vincent',
    title: 'Idées Vincent',
    responsible: 'Vincent Bourassa',
    avatar: '/VB.png',
    description: 'Espace réservé aux idées, constats et propositions de Vincent.',
  },
  {
    id: 'francis',
    title: 'Idées Francis',
    responsible: 'Francis Marquis',
    avatar: '/FM.png',
    description: 'Espace réservé aux idées, constats et propositions de Francis.',
  },
  {
    id: 'stephane',
    title: 'Idées Stéphane',
    responsible: 'Stéphane Paradis',
    avatar: '/SP.png',
    description: 'Espace réservé aux idées, constats et propositions de Stéphane.',
  },
].sort((a, b) => a.responsible.localeCompare(b.responsible, 'fr', { sensitivity: 'base' }));

export default function LacEpauleTI2026() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-slate-100">
      <Header
        title="Lac à l'épaule décembre 2026"
        subtitle="Préparation stratégique de Service & Direction TI"
      />

      <main className="mx-auto w-full max-w-7xl px-6 pb-14 pt-6 lg:px-8">
        <button
          type="button"
          onClick={() => navigate('/direction-ti')}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-300 hover:text-blue-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à Service & Direction TI
        </button>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-6 overflow-hidden rounded-3xl bg-slate-950 p-5 text-white shadow-xl ring-1 ring-slate-900/10 md:p-6"
          style={{
            background: 'linear-gradient(135deg, #071a33 0%, #0b3f70 55%, #087a83 100%)',
            color: '#ffffff',
          }}
        >
          <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-cyan-400/15 blur-3xl" />
          <div className="absolute -bottom-28 left-1/3 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative max-w-2xl">
            <img
              src="/brand/lac-epaule-ti-logo-white.svg"
              alt="Lac à l'épaule — Service et Direction TI — Décembre 2026"
              className="h-auto w-full max-w-lg"
            />
          </div>
        </motion.section>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600">Sous-section collaborative</p>
              <h2 className="mt-1 text-2xl font-bold text-slate-900">Préparation du Lac à l&apos;épaule</h2>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                Un espace par intervenant pour regrouper ses idées, ses constats et ses propositions avant la rencontre.
              </p>
            </div>
            <div className="inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
              <Layers3 className="h-4 w-4" />
              {subjectCards.length} carte{subjectCards.length === 1 ? '' : 's'}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {subjectCards.map((card, index) => (
              <motion.article
                key={card.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.06 }}
                className="group relative min-h-64 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl"
              >
                <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-blue-200/30 blur-2xl transition group-hover:bg-cyan-200/40" />

                <div className="relative flex h-full flex-col">
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={card.avatar}
                        alt={card.responsible}
                        className="h-16 w-16 rounded-full border-4 border-white object-cover shadow-lg ring-2 ring-blue-200"
                      />
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Intervenant</p>
                        <p className="mt-1 font-semibold text-slate-700">{card.responsible}</p>
                      </div>
                    </div>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 shadow-sm">
                      <Lightbulb className="h-5 w-5" />
                    </div>
                  </div>

                  <h3 className="mb-3 text-2xl font-extrabold text-slate-900">{card.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{card.description}</p>

                  <div className="mt-auto pt-5">
                    <span className="inline-flex rounded-full border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold text-blue-700 shadow-sm">
                      Prête à recevoir ses idées
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}